import { createErrorPayload } from "@/common/utils/error/errorHandler";
import { getCurrentEnvironmentVariables } from "@/lib/CONSTANTS";

// Helper functions for cookie management
function setCookie(name, value, hours) {
  const date = new Date();
  date.setTime(date.getTime() + hours * 60 * 60 * 1000);
  const expires = "expires=" + date.toUTCString();
  document.cookie = name + "=" + value + ";" + expires + ";path=/;SameSite=Strict;Secure";
}

function getCookie(name) {
  const nameEQ = name + "=";
  const ca = document.cookie.split(";");
  for (let i = 0; i < ca.length; i++) {
    let c = ca[i];
    while (c.charAt(0) === " ") c = c.substring(1, c.length);
    if (c.indexOf(nameEQ) === 0) return c.substring(nameEQ.length, c.length);
  }
  return null;
}

function deleteCookie(name) {
  document.cookie = name + "=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;";
}

export async function executeRequest({
  url,
  method,
  body,
  headers = {},
  credentials = "same-origin",
  authorizedRequest = true,
  rawResponse = false,
  headerUpload = false,
  requireGCDHeader = true
}) {
  let token = "";

  if (requireGCDHeader) {
    token = await generateToken();
  }

  if (typeof authorizedRequest !== "boolean") {
    throw new Error("authorizedRequest is missing");
  }

  if (requireGCDHeader) {
    headers["Authorization"] = token;
    headers["consumer-key"] = getCurrentEnvironmentVariables().accessTokenConfiguration.clientId;
  }
  if (!headers["Accept"] && !headerUpload) {
    headers["Accept"] = "application/json";
  }
  if (!headers["Content-Type"] && !headerUpload) {
    headers["Content-Type"] = "application/json";
  }

  let response;
  try {
    response = await fetch(url, {
      method,
      body,
      headers,
      credentials
    });
    if (!checkResponse(response)) {
      throw response;
    }
  } catch (error) {
    throw createErrorPayload(error);
  }

  try {
    if (rawResponse) {
      return response;
    } else {
      // Only return the json if there is a body in the response
      return await response.json();
    }
  } catch (jsonError) {
    let status = response?.status;
    return { status };
  }
}

function checkResponse(response) {
  if (!response) {
    throw new Error("checkResponse: response is missing");
  }
  if ([401, 403].includes(response.status)) {
    //TODO: we can call method to redirect to login if needed
    return false;
  } else {
    return response.ok;
  }
}

// Cache for ongoing token generation to prevent multiple simultaneous calls
let tokenGenerationPromise = null;

async function generateToken() {
  const tokenCookieName = "gcd_access_token";
  const tokenExpiryCookieName = "gcd_token_expiry";

  // Check if token exists and is not expired
  const existingToken = getCookie(tokenCookieName);
  const tokenExpiry = getCookie(tokenExpiryCookieName);

  if (existingToken && tokenExpiry) {
    const expiryTime = new Date(parseInt(tokenExpiry));
    const currentTime = new Date();

    // If token is still valid (not expired), return it
    if (currentTime < expiryTime) {
      return existingToken;
    } else {
      // Token expired, clean up cookies
      deleteCookie(tokenCookieName);
      deleteCookie(tokenExpiryCookieName);
    }
  }

  // If token generation is already in progress, wait for it
  if (tokenGenerationPromise) {
    return await tokenGenerationPromise;
  }

  // Generate new token
  tokenGenerationPromise = (async () => {
    const url = getCurrentEnvironmentVariables().accessTokenConfiguration?.accessTokenEndPoint;
    const options = {
      method: "POST",
      headers: {
        "cache-control": "no-cache",
        "content-type": "application/x-www-form-urlencoded"
      },
      body: new URLSearchParams({
        client_id: getCurrentEnvironmentVariables().accessTokenConfiguration.clientId,
        client_secret: getCurrentEnvironmentVariables().accessTokenConfiguration.clientSecret,
        grant_type: "client_credentials"
      })
    };

    try {
      const response = await fetch(url, options);

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();

      // Validate the response contains required token data
      if (!data.token_type || !data.access_token) {
        throw new Error("Invalid token response: missing token_type or access_token.");
      }

      const token = data.token_type + " " + data.access_token;

      // Store token and expiry time in cookies (1.5 hours = 1.5)
      setCookie(tokenCookieName, token, 1.5);

      // Store expiry timestamp for validation
      const expiryTime = new Date();
      expiryTime.setTime(expiryTime.getTime() + 1.5 * 60 * 60 * 1000);
      setCookie(tokenExpiryCookieName, expiryTime.getTime().toString(), 1.5);

      return token;
    } finally {
      // Clear the promise cache once generation is complete (success or failure)
      tokenGenerationPromise = null;
    }
  })();

  return await tokenGenerationPromise;
}
